<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-file-spreadsheet"></i>
        {{ $t("fleetReports.daily_title") }}
      </h4>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("fleetReports.from_date") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="fromDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-2">
          <div class="control field">
            <label class="label">{{ $t("common.ຫາວັນທີ") }}:</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="toDate"
                :clearable="false"
                @change="FetchReport"
            ></date-picker>
          </div>
        </div>
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetReports.route") }}</label>
            <model-list-select
                :list="boardRoutes"
                v-model="selectedRouteId"
                :placeholder="$t('common.ທັງໝົດ')"
                option-text="name"
                option-value="id"
                @input="FetchReport"
            >
            </model-list-select>
          </div>
        </div>
        <div class="column is-2" :style="{display: 'flex', alignItems: 'flex-end'}">
          <vs-button flat :loading="exportLoading" @click="ExportReport()">
            <i class="fal fa-file-export" :style="{marginRight: '5px'}"></i> Export Excel
          </vs-button>
        </div>
      </div>
    </div>

    <div class="center table-scroll">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("fleetReports.date") }}</vs-th>
            <vs-th>{{ $t("fleetReports.route") }}</vs-th>
            <vs-th>{{ $t("fleetReports.trips") }}</vs-th>
            <vs-th>{{ $t("fleetReports.started_trips") }}</vs-th>
            <vs-th>{{ $t("fleetReports.closed_trips") }}</vs-th>
            <vs-th>{{ $t("fleetReports.bills") }}</vs-th>
            <vs-th>{{ $t("fleetReports.pieces") }}</vs-th>
            <vs-th>{{ $t("fleetReports.weight_kg") }}</vs-th>
            <vs-th>m³</vs-th>
            <vs-th>{{ $t("fleetReports.no_dimension") }}</vs-th>
            <vs-th>{{ $t("fleetReports.off_route_pieces") }}</vs-th>
            <vs-th>{{ $t("fleetReports.utilization_pct") }}</vs-th>
            <vs-th>{{ $t("fleetReports.distance_km") }}</vs-th>
            <vs-th>{{ $t("fleetReports.standard_fuel_liters") }}</vs-th>
            <vs-th>{{ $t("fleetReports.actual_fuel_liters") }}</vs-th>
            <!-- Money columns: dropped, not blanked, for a planner without the
                 fleet-cost permission — the API withholds these keys, and six
                 empty headers would read as "this route costs nothing". -->
            <template v-if="CanViewCost">
              <vs-th>{{ $t("fleetReports.standard_fuel_amount") }}</vs-th>
              <vs-th>{{ $t("fleetReports.actual_fuel_amount") }}</vs-th>
              <vs-th>{{ $t("fleetReports.contract_cost") }}</vs-th>
              <vs-th>{{ $t("fleetReports.cost_per_bill") }}</vs-th>
              <vs-th>{{ $t("fleetReports.cost_per_kg") }}</vs-th>
              <vs-th>{{ $t("fleetReports.cost_per_cbm") }}</vs-th>
            </template>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" :data="row" v-for="(row, index) in rows">
            <vs-td>{{ FleetFormatDate(row.date) }}</vs-td>
            <vs-td :style="{color: '#195BFF'}">{{ row.route_name }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">
              {{ row.trips }}
              <div v-if="row.outbound_trips !== undefined" :style="{fontSize: '10px'}">
                <span :style="{color: FleetDirectionColor('outbound')}">{{ $t("fleetCommon.direction_outbound") }} {{ row.outbound_trips }}</span> /
                <span :style="{color: FleetDirectionColor('return')}">{{ $t("fleetCommon.direction_return") }} {{ row.return_trips }}</span>
              </div>
            </vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ NullableNumber(row.started_trips) }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ row.closed_trips }}</vs-td>
            <vs-td>{{ row.bills }}</vs-td>
            <vs-td>{{ row.pieces }}</vs-td>
            <vs-td>{{ FleetFormatNumber(row.weight_kg, 1) }}</vs-td>
            <vs-td>{{ row.cbm }}</vs-td>
            <vs-td :style="{textAlign: 'center'}">{{ NullableNumber(row.no_dimension_count) }}</vs-td>
            <vs-td :style="{textAlign: 'center', color: row.off_route_pieces ? '#e8a10f' : '', fontWeight: row.off_route_pieces ? 'bold' : ''}">{{ row.off_route_pieces || 0 }}</vs-td>
            <vs-td>{{ NullablePct(row.utilization_pct) }}</vs-td>
            <vs-td>{{ row.distance_km || '...' }}</vs-td>
            <vs-td>{{ NullableNumber(row.standard_fuel_liters, 1) }}</vs-td>
            <vs-td>{{ NullableNumber(row.actual_fuel_liters, 1) }}</vs-td>
            <template v-if="CanViewCost">
              <vs-td>{{ NullableNumber(row.standard_fuel_amount) }}</vs-td>
              <vs-td>{{ NullableNumber(row.actual_fuel_amount) }}</vs-td>
              <vs-td>{{ NullableNumber(row.contract_cost) }}</vs-td>
              <vs-td>{{ NullableNumber(row.cost_per_bill) }}</vs-td>
              <vs-td>{{ NullableNumber(row.cost_per_kg) }}</vs-td>
              <vs-td>{{ NullableNumber(row.cost_per_cbm) }}</vs-td>
            </template>
          </vs-tr>
          <vs-tr v-if="totals && rows.length">
            <vs-td :style="totalStyle">{{ $t("fleetReports.total") }}</vs-td>
            <vs-td :style="totalStyle"></vs-td>
            <vs-td :style="totalStyle">{{ totals.trips }}</vs-td>
            <vs-td :style="totalStyle">{{ NullableNumber(totals.started_trips) }}</vs-td>
            <vs-td :style="totalStyle">{{ totals.closed_trips }}</vs-td>
            <vs-td :style="totalStyle">{{ totals.bills }}</vs-td>
            <vs-td :style="totalStyle">{{ totals.pieces }}</vs-td>
            <vs-td :style="totalStyle">{{ FleetFormatNumber(totals.weight_kg, 1) }}</vs-td>
            <vs-td :style="totalStyle">{{ totals.cbm }}</vs-td>
            <vs-td :style="totalStyle">{{ NullableNumber(totals.no_dimension_count) }}</vs-td>
            <vs-td :style="[totalStyle, {color: totals.off_route_pieces ? '#e8a10f' : ''}]">{{ totals.off_route_pieces || 0 }}</vs-td>
            <vs-td :style="totalStyle">{{ NullablePct(totals.utilization_pct) }}</vs-td>
            <vs-td :style="totalStyle">{{ NullableNumber(totals.distance_km, 1) }}</vs-td>
            <vs-td :style="totalStyle">{{ NullableNumber(totals.standard_fuel_liters, 1) }}</vs-td>
            <vs-td :style="totalStyle">{{ NullableNumber(totals.actual_fuel_liters, 1) }}</vs-td>
            <template v-if="CanViewCost">
              <vs-td :style="totalStyle">{{ NullableNumber(totals.standard_fuel_amount) }}</vs-td>
              <vs-td :style="totalStyle">{{ NullableNumber(totals.actual_fuel_amount) }}</vs-td>
              <vs-td :style="totalStyle">{{ NullableNumber(totals.contract_cost) }}</vs-td>
              <vs-td :style="totalStyle">{{ NullableNumber(totals.cost_per_bill) }}</vs-td>
              <vs-td :style="totalStyle">{{ NullableNumber(totals.cost_per_kg) }}</vs-td>
              <vs-td :style="totalStyle">{{ NullableNumber(totals.cost_per_cbm) }}</vs-td>
            </template>
          </vs-tr>
        </template>
        <template #footer>
          <Loading v-if="isLoadingReport"/>
          <p v-if="!isLoadingReport && !rows.length"
             :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
            {{ $t("fleetReports.no_data_in_range") }}
          </p>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';

export default {
  components: {
    DatePicker,
    ModelListSelect,
    Loading,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    fromDate: moment(new Date()).startOf('month').format('YYYY-MM-DD'),
    toDate: moment(new Date()).format('YYYY-MM-DD'),
    selectedRouteId: '',
    rows: [],
    totals: null,
    isLoadingReport: false,
    exportLoading: false,
    totalStyle: {fontWeight: 'bold', background: '#f7f9ff'},
  }),
  methods: {
    NullableNumber(value, digits = 0) {
      if (value === null || value === undefined) return '...';
      return this.FleetFormatNumber(value, digits);
    },
    NullablePct(value) {
      if (value === null || value === undefined) return '...';
      return `${value}%`;
    },
    ReportParams() {
      return {
        from: this.fromDate,
        to: this.toDate,
        route_id: this.selectedRouteId || undefined,
      };
    },
    FetchReport() {
      this.isLoadingReport = true;
      this.FetchDailyReport(this.ReportParams()).then((res) => {
        const data = res.data.data || {};
        this.rows = data.rows || [];
        this.totals = data.totals || null;
        this.isLoadingReport = false;
      }).catch((error) => {
        this.rows = [];
        this.totals = null;
        this.isLoadingReport = false;
        // Surface range errors (RANGE_INVALID / RANGE_TOO_LARGE) instead of
        // silently rendering the normal empty state.
        this.$notification.error({message: this.FleetErrorText(error, this.$t('fleetReports.fetch_failed'))});
      });
    },
    ExportReport() {
      this.exportLoading = true;
      this.ExportDailyReport({from: this.fromDate, to: this.toDate, route_id: this.selectedRouteId || undefined})
          .then(() => {
            this.exportLoading = false;
          })
          .catch(() => {
            this.exportLoading = false;
            this.$notification.error({message: this.$t('fleetReports.export_failed')});
          });
    },
  },
  created() {
    this.FetchBoardRoutes();
    this.FetchReport();
  },
};
</script>

<style scoped>
.table-scroll {
  overflow-x: auto;
}
</style>
